import { Flex } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatMoneyValue } from '@/shared/lib/money'
import { LoadError } from '@/shared/ui/LoadError'
import { AmountCard } from './AmountCard'
import { FUND_WINDOW_KINDS, perTxnLimit, remainingOf, type FundWindowKind } from './profileApi'
import type { FundLimitsState } from './useMyAccount'

/**
 * Kunlik va oylik PUL chegarasi — «yana qancha qoldi».
 *
 * <h2>⚠️ ASOSIY SON — QOLGANI, chegara emas</h2>
 * Kassir bu blokka kun o'rtasida bitta savol bilan qaraydi: «yana qancha
 * to'ldira olaman». Chegaraning o'zi («10 000 000») bu savolga javob
 * bermaydi — u sarflanganini bilmaguncha ma'nosiz. Shuning uchun katta
 * raqam QOLDIQ, chegara va sarflangani esa ostidagi kichik qator.
 *
 * <h2>⚠️ QOLDIQNI PANEL HISOBLAYDI va bu majburiy</h2>
 * `FundLimits.remainingOf` — argumentli METOD, ya'ni Jackson uni JSON'ga
 * chiqarmaydi ({@link remainingOf} dagi izoh). Formula serverdagining
 * aynan o'zi.
 *
 * <h2>⚠️ «Cheklov yo'q» va «nol» BOSHQA-BOSHQA</h2>
 * `null` chegara — cheksiz, `0` — umuman ruxsat yo'q (`FundLimits`
 * javadoc'i). Ikkalasiga `0` yozish sherikka «bugun ishlay olmayman»
 * degan yolg'on aytardi.
 *
 * <h2>⚠️ VALYUTA `/balance` DAN</h2>
 * Chegara javobida valyuta YO'Q (yalang'och minor son). Balans
 * kelmaguncha son ko'rsatilmaydi: taxminiy valyuta bilan formatlash
 * summani boshqa kattalikda chizardi.
 *
 * ⚠️ `used` FAQAT abonent to'ldirishida o'sadi — depozit to'ldirish ham,
 * komissiya ham uni yemaydi (`FundLimitRepository`). Ya'ni bu chegara
 * cashback bilan bog'liq emas.
 */
export function FundLimitsPanel({ state }: { state: FundLimitsState }) {
  const { t } = useTranslation()

  if (state.error) {
    return <LoadError titleKey="profile.limitsFailed" error={state.error} />
  }

  const { limits, currency } = state
  const asMoney = (minor: number) =>
    currency === null ? t('common.dash') : formatMoneyValue({ amount: minor, currency })

  const card = (kind: FundWindowKind) => {
    const limit = limits?.limits[kind] ?? null
    const used = limits?.used[kind] ?? 0
    const remaining = limits === null ? null : remainingOf(limits, kind)

    return (
      <AmountCard
        key={kind}
        label={t(`profile.limitKind.${kind}`)}
        value={
          limits === null
            ? t('common.dash')
            : remaining === null
              ? t('profile.limitNone')
              : asMoney(remaining)
        }
        hint={
          limits === null
            ? undefined
            : limit === null
              ? t('profile.limitUsedOnly', { used: asMoney(used) })
              : t('profile.limitHint', { limit: asMoney(limit), used: asMoney(used) })
        }
        // Chegara TUGAGANDA rang o'zgaradi: kassir buni kassa ekranida
        // rad javobini olishdan OLDIN ko'rishi kerak (§7 dagi oxirgi qator).
        tone={remaining === 0 ? 'danger' : 'default'}
        loading={state.loading}
      />
    )
  }

  /**
   * ⚠️ BITTA TO'LOV SHIFTI — «qolgani» YO'Q va bo'lishi ham mumkin emas.
   *
   * `PER_TXN` oyna limiti emas: u har amalda qaytadan qo'llanadi va
   * hech qachon «sarflanmaydi». Uni kunlik/oylik bilan bir xil qilib
   * «yana shuncha qoldi» deb chizish YOLG'ON son bo'lardi — kun davomida
   * kamayadigan raqamdek ko'rinib, aslida hech qachon kamaymasdi.
   * Shuning uchun katta son CHEGARANING O'ZI va yorliq boshqacha
   * o'qiladi: «bitta to'lovda ko'pi bilan …».
   */
  const perTxn = limits === null ? null : perTxnLimit(limits)

  return (
    <Flex gap={10} wrap>
      {FUND_WINDOW_KINDS.map(card)}
      <AmountCard
        label={t('profile.limitKind.PER_TXN')}
        value={
          limits === null
            ? t('common.dash')
            : perTxn === null
              ? t('profile.limitNone')
              : asMoney(perTxn)
        }
        hint={limits === null || perTxn === null ? undefined : t('profile.perTxnHint')}
        loading={state.loading}
      />
    </Flex>
  )
}
